import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Modal, Spinner } from 'react-bootstrap';
import { FaExpand, FaImage } from 'react-icons/fa';
import config from 'config';
import './LogoPreview.css';

const DEBOUNCE_MS = 500;

const STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  FOUND: 'found',
  NOT_FOUND: 'not_found',
};

const resolveImgStatus = (img) => {
  if (!img) return STATUS.CHECKING;
  if (!img.complete) return STATUS.CHECKING;
  return img.naturalWidth > 0 ? STATUS.FOUND : STATUS.NOT_FOUND;
};

/**
 * Debounced preview of a logo key against the public logos S3 bucket.
 * Renders nothing useful when config.logoBucketBaseUrl is unset.
 */
const LogoPreview = ({ logoKey }) => {
  const baseUrl = config.logoBucketBaseUrl;
  const [debouncedKey, setDebouncedKey] = useState('');
  const [loadGeneration, setLoadGeneration] = useState(0);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [showLightbox, setShowLightbox] = useState(false);
  const activeGenerationRef = useRef(0);

  useEffect(() => {
    const trimmed = (logoKey || '').trim();
    if (!trimmed) {
      activeGenerationRef.current += 1;
      setDebouncedKey('');
      setStatus(STATUS.IDLE);
      return undefined;
    }

    setStatus(STATUS.CHECKING);
    const timer = setTimeout(() => {
      // Bump generation even when the key is unchanged so cached/same-URL
      // edits don't leave us stuck on "Checking…" (React skips setState if
      // the value is identical, which would skip remounting the <img>).
      activeGenerationRef.current += 1;
      setLoadGeneration(activeGenerationRef.current);
      setDebouncedKey(trimmed);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [logoKey]);

  const imageUrl = useMemo(() => {
    if (!baseUrl || !debouncedKey) return null;
    const encodedKey = debouncedKey
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    return `${baseUrl}/${encodedKey}`;
  }, [baseUrl, debouncedKey]);

  const applyStatusForGeneration = useCallback((generation, nextStatus) => {
    if (generation !== activeGenerationRef.current) return;
    setStatus(nextStatus);
  }, []);

  const handleImgRef = useCallback(
    (img) => {
      if (!img || !loadGeneration) return;
      const resolved = resolveImgStatus(img);
      if (resolved !== STATUS.CHECKING) {
        applyStatusForGeneration(loadGeneration, resolved);
      }
    },
    [applyStatusForGeneration, loadGeneration]
  );

  if (!baseUrl) {
    return (
      <div className="logo-preview logo-preview--unconfigured mt-2">
        <span className="text-muted small">
          Logo preview unavailable — <code>config.logoBucketBaseUrl</code> is not configured.
        </span>
      </div>
    );
  }

  if (!debouncedKey && status === STATUS.IDLE) {
    return (
      <div className="logo-preview logo-preview--empty mt-2">
        <FaImage className="logo-preview__placeholder-icon" />
        <span className="text-muted small">Enter a logo file name to preview</span>
      </div>
    );
  }

  return (
    <div className="logo-preview mt-2">
      <div className="logo-preview__row">
        <div className="logo-preview__thumb-wrap">
          {status === STATUS.CHECKING && (
            <div className="logo-preview__checking">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {status === STATUS.NOT_FOUND && (
            <div className="logo-preview__missing">
              <FaImage />
            </div>
          )}
          {imageUrl && loadGeneration > 0 && (
            <img
              key={`${imageUrl}#${loadGeneration}`}
              ref={handleImgRef}
              src={imageUrl}
              alt="Logo preview"
              className={`logo-preview__thumb ${status === STATUS.FOUND ? 'is-visible' : 'is-loading'}`}
              onLoad={(event) =>
                applyStatusForGeneration(loadGeneration, resolveImgStatus(event.currentTarget))
              }
              onError={() => applyStatusForGeneration(loadGeneration, STATUS.NOT_FOUND)}
            />
          )}
        </div>
        <div className="logo-preview__meta">
          {status === STATUS.CHECKING && (
            <Badge bg="secondary">Checking…</Badge>
          )}
          {status === STATUS.FOUND && (
            <>
              <Badge bg="success">Found</Badge>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ml-2 logo-preview__expand"
                onClick={() => setShowLightbox(true)}
              >
                <FaExpand /> View full size
              </button>
            </>
          )}
          {status === STATUS.NOT_FOUND && (
            <Badge bg="warning" text="dark">
              Not found at this path
            </Badge>
          )}
          {debouncedKey && (
            <div className="logo-preview__key text-muted small mt-1">{debouncedKey}</div>
          )}
          {imageUrl && (
            <div className="logo-preview__url text-muted small" title={imageUrl}>
              {imageUrl}
            </div>
          )}
        </div>
      </div>

      <Modal show={showLightbox} onHide={() => setShowLightbox(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Logo preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {imageUrl && (
            <img src={imageUrl} alt="Logo full size" className="logo-preview__lightbox-img" />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LogoPreview;
